import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faTachometerAlt, faChurch, faCogs, faListAlt,
  faFileInvoiceDollar, faUsers, faFileImport, faSignOutAlt,
  faCalendarAlt, faMoneyCheckAlt, faVoteYea
} from '@fortawesome/free-solid-svg-icons';
import './Navbar.css';

const Navbar = ({ ministries, selectedYear, onYearChange }) => {
  const { user, logout, isAdmin, isPayer, isSecretary } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ministriesDropdownOpen, setMinistriesDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  const ministriesRef = useRef(null);
  const adminRef = useRef(null);

  // Fechar tudo ao mudar de rota
  useEffect(() => {
    setMobileMenuOpen(false);
    setMinistriesDropdownOpen(false);
    setAdminDropdownOpen(false);
  }, [location.pathname]);

  // Fechar dropdowns ao clicar/tocar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ministriesRef.current && !ministriesRef.current.contains(e.target)) {
        setMinistriesDropdownOpen(false);
      }
      if (adminRef.current && !adminRef.current.contains(e.target)) {
        setAdminDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Fechar menu ao pressionar Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setMinistriesDropdownOpen(false);
        setAdminDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (mobileMenuOpen) {
      setMinistriesDropdownOpen(false);
      setAdminDropdownOpen(false);
    }
  };

  const closeAll = () => {
    setMobileMenuOpen(false);
    setMinistriesDropdownOpen(false);
    setAdminDropdownOpen(false);
  };

  const toggleMinistriesDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMinistriesDropdownOpen((prev) => !prev);
    setAdminDropdownOpen(false);
  };

  const toggleAdminDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdminDropdownOpen((prev) => !prev);
    setMinistriesDropdownOpen(false);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-header">
          <div className="navbar-brand">
            <Link to="/" onClick={closeAll} title="Voltar ao painel principal">
              <img src="/iasd-icon.png" alt="IASD" className="navbar-brand-icon" />
              Tesouraria Igreja
            </Link>
          </div>
          
          <button 
            className="navbar-toggle" 
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
            title={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Overlay para fechar o menu mobile ao tocar fora */}
        {mobileMenuOpen && (
          <div className="navbar-overlay" onClick={closeAll} aria-hidden="true" />
        )}

        <div className={`navbar-content ${mobileMenuOpen ? 'active' : ''}`}>
          <div className="navbar-menu">
            {!isPayer && !isSecretary && (
              <Link to="/" className="navbar-link" onClick={closeAll} title="Visão geral de todos os ministérios">
                <span className="navbar-link-inner">
                  <FontAwesomeIcon icon={faTachometerAlt} className="navbar-link-icon" />
                  Dashboard
                </span>
              </Link>
            )}

            {isPayer && (
              <Link to="/payer" className="navbar-link" onClick={closeAll} title="Ver pagamentos pendentes">
                <span className="navbar-link-inner">
                  <FontAwesomeIcon icon={faMoneyCheckAlt} className="navbar-link-icon" />
                  Pagamentos Pendentes
                </span>
              </Link>
            )}

            {isSecretary && (
              <Link to="/admin/votacoes" className="navbar-link" onClick={closeAll} title="Gerenciar as votações de nomeação de líderes">
                <span className="navbar-link-inner">
                  <FontAwesomeIcon icon={faVoteYea} className="navbar-link-icon" />
                  Votações
                </span>
              </Link>
            )}

            {ministries && ministries.length > 0 && !isPayer && (
              <div className="navbar-dropdown" ref={ministriesRef}>
                <button 
                  className="navbar-link dropdown-toggle" 
                  onClick={toggleMinistriesDropdown}
                  aria-expanded={ministriesDropdownOpen}
                  aria-haspopup="true"
                  title="Selecione um ministério para ver detalhes"
                >
                  <span className="navbar-link-inner">
                    <FontAwesomeIcon icon={faChurch} className="navbar-link-icon" />
                    Ministérios
                  </span>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className={`dropdown-arrow ${ministriesDropdownOpen ? 'active' : ''}`}
                  />
                </button>
                <div 
                  className={`navbar-dropdown-content ${ministriesDropdownOpen ? 'active' : ''}`}
                  role="menu"
                >
                  {ministries.length > 5 && (
                    <div className="dropdown-scroll-hint">
                      <FontAwesomeIcon icon={faListAlt} />
                      <span>{ministries.length} ministérios — role para ver todos</span>
                    </div>
                  )}
                  {ministries.map((ministry) => (
                    <Link
                      key={ministry._id}
                      to={`/ministry/${ministry._id}`}
                      className="navbar-dropdown-item"
                      onClick={closeAll}
                      role="menuitem"
                      title={`Ver detalhes de ${ministry.name}`}
                    >
                      {ministry.logoUrl ? (
                        <img src={ministry.logoUrl} alt="" className="navbar-ministry-logo" />
                      ) : (
                        <span className="navbar-ministry-logo-fallback">
                          <i className="fas fa-church"></i>
                        </span>
                      )}
                      {ministry.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="navbar-dropdown" ref={adminRef}>
                <button 
                  className="navbar-link dropdown-toggle" 
                  onClick={toggleAdminDropdown}
                  aria-expanded={adminDropdownOpen}
                  aria-haspopup="true"
                  title="Opções de administração do sistema"
                >
                  <span className="navbar-link-inner">
                    <FontAwesomeIcon icon={faCogs} className="navbar-link-icon" />
                    Administração
                  </span>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className={`dropdown-arrow ${adminDropdownOpen ? 'active' : ''}`}
                  />
                </button>
                <div 
                  className={`navbar-dropdown-content ${adminDropdownOpen ? 'active' : ''}`}
                  role="menu"
                >
                  <Link to="/admin/ministries" className="navbar-dropdown-item" onClick={closeAll} role="menuitem" title="Gerenciar ministérios da igreja">
                    <FontAwesomeIcon icon={faChurch} className="dropdown-item-icon" />
                    Ministérios
                  </Link>
                  <Link to="/admin/reimbursements" className="navbar-dropdown-item" onClick={closeAll} role="menuitem" title="Gerenciar solicitações de reembolso">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="dropdown-item-icon" />
                    Reembolsos
                  </Link>
                  <Link to="/admin/users" className="navbar-dropdown-item" onClick={closeAll} role="menuitem" title="Gerenciar usuários do sistema">
                    <FontAwesomeIcon icon={faUsers} className="dropdown-item-icon" />
                    Usuários
                  </Link>
                  <Link to="/admin/import-offerings" className="navbar-dropdown-item" onClick={closeAll} role="menuitem" title="Importar saldos de ofertas via PDF">
                    <FontAwesomeIcon icon={faFileImport} className="dropdown-item-icon" />
                    Importar Saldos
                  </Link>
                  <Link to="/admin/votacoes" className="navbar-dropdown-item" onClick={closeAll} role="menuitem" title="Votações de nomeação de líderes">
                    <FontAwesomeIcon icon={faVoteYea} className="dropdown-item-icon" />
                    Votações
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="navbar-actions">
            <div className="year-selector-wrapper" title="Selecione o ano fiscal para visualização">
              <label htmlFor="year-select" className="year-label hide-mobile">
                <FontAwesomeIcon icon={faCalendarAlt} className="year-icon" />
                Ano:
              </label>
              <select
                id="year-select"
                className="year-selector"
                value={selectedYear}
                onChange={(e) => {
                  onYearChange(parseInt(e.target.value));
                  closeAll();
                }}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="navbar-user">
              <span className="user-name" title={`Logado como ${user?.username}`}>
                <i className="fas fa-user-circle"></i>
                {user?.username}
              </span>
              <button 
                onClick={handleLogout} 
                className="btn btn-sm btn-secondary btn-logout"
                title="Encerrar sessão"
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
